/**
 * Bitmask candidate solver for 9x9 Latin-square-with-boxes problems.
 *
 * This file backs the uniqueness guarantee for Sudoku and Killer Sudoku and
 * supplies the technique tier that becomes the difficulty rating. Constraint
 * propagation runs in tier order; `countSolutions` is the DFS fallback that
 * stops at a cap, which is how we prove "exactly one solution" cheaply.
 */
export const N = 9;
export const CELLS = 81;
export const ALL = 0x1ff; // 9 candidate bits
/* ---------------- bit helpers ---------------- */
const POPCOUNT = new Uint8Array(512);
for (let i = 0; i < 512; i++)
    POPCOUNT[i] = (i & 1) + POPCOUNT[i >> 1];
export const bit = (d) => 1 << (d - 1);
export const popcount = (mask) => POPCOUNT[mask];
/** Lowest set digit of a mask (1..9), or 0 for an empty mask. */
export function lowestDigit(mask) {
    if (mask === 0)
        return 0;
    return 31 - Math.clz32(mask & -mask) + 1;
}
export function maskDigits(mask) {
    const out = [];
    for (let d = 1; d <= 9; d++)
        if (mask & bit(d))
            out.push(d);
    return out;
}
/* ---------------- geometry ---------------- */
export const rowOf = (i) => (i / 9) | 0;
export const colOf = (i) => i % 9;
export const boxOf = (i) => ((rowOf(i) / 3) | 0) * 3 + ((colOf(i) / 3) | 0);
/** 27 units (9 rows, 9 cols, 9 boxes), each a list of 9 cell indices. */
export const UNITS = (() => {
    const units = [];
    for (let r = 0; r < 9; r++)
        units.push(Array.from({ length: 9 }, (_, c) => r * 9 + c));
    for (let c = 0; c < 9; c++)
        units.push(Array.from({ length: 9 }, (_, r) => r * 9 + c));
    for (let b = 0; b < 9; b++) {
        const br = ((b / 3) | 0) * 3;
        const bc = (b % 3) * 3;
        const cells = [];
        for (let dr = 0; dr < 3; dr++)
            for (let dc = 0; dc < 3; dc++)
                cells.push((br + dr) * 9 + bc + dc);
        units.push(cells);
    }
    return units;
})();
export const ROW_UNITS = UNITS.slice(0, 9);
export const COL_UNITS = UNITS.slice(9, 18);
export const BOX_UNITS = UNITS.slice(18, 27);
/** The 20 cells that share a row, column or box with cell i. */
export const PEERS = (() => {
    const peers = [];
    for (let i = 0; i < CELLS; i++) {
        const s = new Set();
        for (const u of UNITS)
            if (u.includes(i))
                for (const j of u)
                    if (j !== i)
                        s.add(j);
        peers.push([...s]);
    }
    return peers;
})();
/**
 * Cage propagation: enumerate every distinct-digit assignment of the cage that
 * hits the target sum and is compatible with current candidates, then reduce
 * each cell to the union of digits that appear in at least one such assignment.
 * Cages are size 2..5, so the enumeration is tiny.
 */
export function cageConstraint(cages) {
    // Enumerating a cage is pure in (cell masks, sum), and propagation revisits
    // the same cage states constantly, so memoising it is the single biggest win
    // in Killer generation.
    const memo = new Map();
    return {
        propagate(cands) {
            let changed = false;
            for (let cageIdx = 0; cageIdx < cages.length; cageIdx++) {
                const cage = cages[cageIdx];
                const k = cage.cells.length;
                let key = `${cageIdx}`;
                for (const c of cage.cells)
                    key += `,${cands[c]}`;
                const cached = memo.get(key);
                if (cached !== undefined) {
                    if (cached === null)
                        return 'contradiction';
                    for (let z = 0; z < k; z++) {
                        const cell = cage.cells[z];
                        const next = cached[z];
                        if (next !== cands[cell]) {
                            cands[cell] = next;
                            changed = true;
                        }
                    }
                    continue;
                }
                const union = new Array(k).fill(0);
                let any = false;
                const dfs = (idx, used, sum, chosen) => {
                    if (sum > cage.sum)
                        return;
                    const remaining = k - idx;
                    if (remaining === 0) {
                        if (sum !== cage.sum)
                            return;
                        any = true;
                        for (let z = 0; z < k; z++)
                            union[z] = union[z] | bit(chosen[z]);
                        return;
                    }
                    // Bound: cheapest and dearest completions with distinct unused digits.
                    let minAdd = 0;
                    let maxAdd = 0;
                    let cnt = 0;
                    for (let d = 1; d <= 9 && cnt < remaining; d++)
                        if (!(used & bit(d))) {
                            minAdd += d;
                            cnt++;
                        }
                    cnt = 0;
                    for (let d = 9; d >= 1 && cnt < remaining; d--)
                        if (!(used & bit(d))) {
                            maxAdd += d;
                            cnt++;
                        }
                    if (sum + minAdd > cage.sum || sum + maxAdd < cage.sum)
                        return;
                    const mask = cands[cage.cells[idx]];
                    for (let d = 1; d <= 9; d++) {
                        const b = bit(d);
                        if (!(mask & b) || used & b)
                            continue;
                        chosen[idx] = d;
                        dfs(idx + 1, used | b, sum + d, chosen);
                    }
                };
                dfs(0, 0, 0, new Array(k).fill(0));
                if (!any) {
                    memo.set(key, null);
                    return 'contradiction';
                }
                const resolved = new Int32Array(k);
                for (let z = 0; z < k; z++) {
                    const cell = cage.cells[z];
                    const next = cands[cell] & union[z];
                    if (next === 0) {
                        memo.set(key, null);
                        return 'contradiction';
                    }
                    resolved[z] = next;
                }
                memo.set(key, resolved);
                for (let z = 0; z < k; z++) {
                    const cell = cage.cells[z];
                    const next = resolved[z];
                    if (next !== cands[cell]) {
                        cands[cell] = next;
                        changed = true;
                    }
                }
            }
            return changed ? 'changed' : 'none';
        },
        validate(grid) {
            for (const cage of cages) {
                let sum = 0;
                const seen = new Set();
                for (const c of cage.cells) {
                    const v = grid[c];
                    if (v === 0)
                        return false;
                    if (seen.has(v))
                        return false;
                    seen.add(v);
                    sum += v;
                }
                if (sum !== cage.sum)
                    return false;
            }
            return true;
        },
    };
}
/* ---------------- candidate grid ---------------- */
export function candidatesFrom(grid) {
    const cands = new Int16Array(CELLS).fill(ALL);
    for (let i = 0; i < CELLS; i++) {
        const v = grid[i];
        if (v === 0)
            continue;
        cands[i] = bit(v);
    }
    // Eliminate givens from peers.
    for (let i = 0; i < CELLS; i++) {
        const v = grid[i];
        if (v === 0)
            continue;
        const b = bit(v);
        for (const p of PEERS[i]) {
            if (p === i)
                continue;
            if (cands[p] & b) {
                cands[p] = cands[p] & ~b;
                if (cands[p] === 0)
                    return null;
            }
        }
    }
    return cands;
}
export function gridFromCandidates(cands) {
    const g = new Array(CELLS).fill(0);
    for (let i = 0; i < CELLS; i++) {
        if (popcount(cands[i]) === 1)
            g[i] = lowestDigit(cands[i]);
    }
    return g;
}
function isSolved(cands) {
    for (let i = 0; i < CELLS; i++)
        if (popcount(cands[i]) !== 1)
            return false;
    return true;
}
/* ---------------- techniques, by tier ---------------- */
/** Tier 1: a cell with one candidate eliminates that digit from its peers. */
function nakedSingle(cands) {
    let changed = false;
    for (let i = 0; i < CELLS; i++) {
        const m = cands[i];
        if (popcount(m) !== 1)
            continue;
        for (const p of PEERS[i]) {
            if (cands[p] & m) {
                cands[p] = cands[p] & ~m;
                if (cands[p] === 0)
                    return 'contradiction';
                changed = true;
            }
        }
    }
    return changed ? 'changed' : 'none';
}
/** Tier 2: a digit with only one home in a unit belongs there. */
function hiddenSingle(cands) {
    let changed = false;
    for (const unit of UNITS) {
        for (let d = 1; d <= 9; d++) {
            const b = bit(d);
            let where = -1;
            let count = 0;
            for (const c of unit) {
                if (cands[c] & b) {
                    count++;
                    where = c;
                    if (count > 1)
                        break;
                }
            }
            if (count === 0)
                return 'contradiction';
            if (count === 1 && popcount(cands[where]) > 1) {
                cands[where] = b;
                changed = true;
            }
        }
    }
    return changed ? 'changed' : 'none';
}
/** Tier 3: k cells in a unit sharing exactly k candidates lock them away. */
function nakedSubset(cands, size) {
    let changed = false;
    for (const unit of UNITS) {
        const open = unit.filter((c) => popcount(cands[c]) > 1);
        if (open.length <= size)
            continue;
        const combo = [];
        const walk = (start, mask) => {
            if (changed)
                return;
            if (combo.length === size) {
                if (popcount(mask) !== size)
                    return;
                for (const c of unit) {
                    if (combo.includes(c))
                        continue;
                    const next = cands[c] & ~mask;
                    if (next !== cands[c]) {
                        cands[c] = next;
                        changed = true;
                    }
                }
                return;
            }
            for (let idx = start; idx < open.length; idx++) {
                const c = open[idx];
                const nextMask = mask | cands[c];
                if (popcount(nextMask) > size)
                    continue;
                combo.push(c);
                walk(idx + 1, nextMask);
                combo.pop();
            }
        };
        walk(0, 0);
    }
    for (let i = 0; i < CELLS; i++)
        if (cands[i] === 0)
            return 'contradiction';
    return changed ? 'changed' : 'none';
}
/** Tier 4: pointing pair (box -> line) and box-line reduction (line -> box). */
function pointingAndBoxLine(cands) {
    let changed = false;
    // Pointing: within a box, if digit d lives in only one row/col, clear the rest of that line.
    for (const box of BOX_UNITS) {
        for (let d = 1; d <= 9; d++) {
            const b = bit(d);
            const spots = box.filter((c) => cands[c] & b);
            if (spots.length < 2)
                continue;
            const rows = new Set(spots.map(rowOf));
            const cols = new Set(spots.map(colOf));
            if (rows.size === 1) {
                const r = rows.values().next().value;
                for (const c of ROW_UNITS[r]) {
                    if (box.includes(c))
                        continue;
                    if (cands[c] & b) {
                        cands[c] = cands[c] & ~b;
                        if (cands[c] === 0)
                            return 'contradiction';
                        changed = true;
                    }
                }
            }
            if (cols.size === 1) {
                const cc = cols.values().next().value;
                for (const c of COL_UNITS[cc]) {
                    if (box.includes(c))
                        continue;
                    if (cands[c] & b) {
                        cands[c] = cands[c] & ~b;
                        if (cands[c] === 0)
                            return 'contradiction';
                        changed = true;
                    }
                }
            }
        }
    }
    // Box-line: within a row/col, if digit d lives in only one box, clear the rest of that box.
    for (const line of [...ROW_UNITS, ...COL_UNITS]) {
        for (let d = 1; d <= 9; d++) {
            const b = bit(d);
            const spots = line.filter((c) => cands[c] & b);
            if (spots.length < 2)
                continue;
            const boxes = new Set(spots.map(boxOf));
            if (boxes.size !== 1)
                continue;
            const bx = boxes.values().next().value;
            for (const c of BOX_UNITS[bx]) {
                if (line.includes(c))
                    continue;
                if (cands[c] & b) {
                    cands[c] = cands[c] & ~b;
                    if (cands[c] === 0)
                        return 'contradiction';
                    changed = true;
                }
            }
        }
    }
    return changed ? 'changed' : 'none';
}
/** Tier 5: X-wing on rows and columns. */
function xWing(cands) {
    let changed = false;
    for (const orientation of ['row', 'col']) {
        const lines = orientation === 'row' ? ROW_UNITS : COL_UNITS;
        const cross = orientation === 'row' ? COL_UNITS : ROW_UNITS;
        const idxOf = orientation === 'row' ? colOf : rowOf;
        for (let d = 1; d <= 9; d++) {
            const b = bit(d);
            const pairs = [];
            for (let li = 0; li < 9; li++) {
                const spots = lines[li].filter((c) => cands[c] & b);
                if (spots.length === 2) {
                    pairs.push({ line: li, a: idxOf(spots[0]), z: idxOf(spots[1]) });
                }
            }
            for (let p = 0; p < pairs.length; p++) {
                for (let q = p + 1; q < pairs.length; q++) {
                    const pp = pairs[p];
                    const qq = pairs[q];
                    if (pp.a !== qq.a || pp.z !== qq.z)
                        continue;
                    for (const ci of [pp.a, pp.z]) {
                        for (const c of cross[ci]) {
                            const lineIdx = orientation === 'row' ? rowOf(c) : colOf(c);
                            if (lineIdx === pp.line || lineIdx === qq.line)
                                continue;
                            if (cands[c] & b) {
                                cands[c] = cands[c] & ~b;
                                if (cands[c] === 0)
                                    return 'contradiction';
                                changed = true;
                            }
                        }
                    }
                }
            }
        }
    }
    return changed ? 'changed' : 'none';
}
/**
 * Run techniques in ascending tier order, always preferring the cheapest one
 * that makes progress. `maxTier` is what the difficulty rating reads.
 */
export function propagate(cands, maxAllowedTier = 5, constraint) {
    let maxTier = 0;
    for (;;) {
        if (constraint) {
            const r = constraint.propagate(cands);
            if (r === 'contradiction')
                return { status: 'contradiction', maxTier, cands };
            if (r === 'changed')
                continue;
        }
        const steps = [
            { tier: 1, run: () => nakedSingle(cands) },
            { tier: 2, run: () => hiddenSingle(cands) },
            { tier: 3, run: () => nakedSubset(cands, 2) },
            { tier: 3, run: () => nakedSubset(cands, 3) },
            { tier: 4, run: () => pointingAndBoxLine(cands) },
            { tier: 5, run: () => xWing(cands) },
        ];
        let progressed = false;
        for (const step of steps) {
            if (step.tier > maxAllowedTier)
                break;
            const r = step.run();
            if (r === 'contradiction')
                return { status: 'contradiction', maxTier, cands };
            if (r === 'changed') {
                maxTier = Math.max(maxTier, step.tier);
                progressed = true;
                break;
            }
        }
        if (!progressed) {
            return { status: isSolved(cands) ? 'solved' : 'stuck', maxTier, cands };
        }
        if (isSolved(cands))
            return { status: 'solved', maxTier, cands };
    }
}
/* ---------------- solution counting ---------------- */
/**
 * DFS that stops as soon as `cap` solutions have been found. Callers pass
 * cap = 2 and test `=== 1` to prove uniqueness.
 */
export function countSolutions(grid, cap = 2, constraint, 
/**
 * How deep to propagate at each node. Deeper costs more per node but prunes
 * far more of the tree. Plain Sudoku is fine on singles alone; a given-free
 * Killer board is not — there the deeper tiers pay for themselves many times
 * over, which is why the default rises when a cage constraint is present.
 */
propagationTier = constraint ? 4 : 2, 
/**
 * Abort after this many search nodes and return UNKNOWN. Every caller treats
 * "not exactly 1" as failure, so aborting is always the conservative answer:
 * a Sudoku dig restores the cell, a Killer layout gets discarded. Without it
 * a single pathological cage layout can burn minutes on its own.
 */
nodeBudget = Number.POSITIVE_INFINITY) {
    const start = candidatesFrom(grid);
    if (!start)
        return 0;
    let found = 0;
    let nodes = 0;
    let aborted = false;
    const search = (cands) => {
        if (found >= cap || aborted)
            return;
        if (++nodes > nodeBudget) {
            aborted = true;
            return;
        }
        const work = Int16Array.from(cands);
        const rep = propagate(work, propagationTier, constraint);
        if (rep.status === 'contradiction')
            return;
        if (rep.status === 'solved') {
            if (!constraint || constraint.validate(gridFromCandidates(work)))
                found++;
            return;
        }
        // Branch on the most constrained open cell.
        let best = -1;
        let bestCount = 10;
        for (let i = 0; i < CELLS; i++) {
            const pc = popcount(work[i]);
            if (pc > 1 && pc < bestCount) {
                bestCount = pc;
                best = i;
                if (pc === 2)
                    break;
            }
        }
        if (best === -1)
            return;
        for (const d of maskDigits(work[best])) {
            if (found >= cap || aborted)
                return;
            const next = Int16Array.from(work);
            next[best] = bit(d);
            search(next);
        }
    };
    search(start);
    return aborted ? COUNT_UNKNOWN : found;
}
/** Returned by `countSolutions` when its node budget ran out. Never equals 1. */
export const COUNT_UNKNOWN = -1;
/** Solve fully (first solution found), or null. */
export function solve(grid, constraint) {
    const start = candidatesFrom(grid);
    if (!start)
        return null;
    let result = null;
    const search = (cands) => {
        const work = Int16Array.from(cands);
        const rep = propagate(work, 2, constraint);
        if (rep.status === 'contradiction')
            return false;
        if (rep.status === 'solved') {
            const g = gridFromCandidates(work);
            if (constraint && !constraint.validate(g))
                return false;
            result = g;
            return true;
        }
        let best = -1;
        let bestCount = 10;
        for (let i = 0; i < CELLS; i++) {
            const pc = popcount(work[i]);
            if (pc > 1 && pc < bestCount) {
                bestCount = pc;
                best = i;
                if (pc === 2)
                    break;
            }
        }
        if (best === -1)
            return false;
        for (const d of maskDigits(work[best])) {
            const next = Int16Array.from(work);
            next[best] = bit(d);
            if (search(next))
                return true;
        }
        return false;
    };
    search(start);
    return result;
}
export function tierToDifficulty(tier, solvedByLogic) {
    if (!solvedByLogic)
        return 'expert';
    if (tier <= 2)
        return 'easy';
    if (tier === 3)
        return 'medium';
    if (tier === 4)
        return 'hard';
    return 'expert';
}
export const DIFFICULTY_ORDER = {
    easy: 0,
    medium: 1,
    hard: 2,
    expert: 3,
};
/**
 * The highest technique tier required before any guessing. A puzzle that the
 * technique set cannot finish requires a guess and is therefore `expert`.
 */
export function rateDifficulty(grid, constraint) {
    const cands = candidatesFrom(grid);
    if (!cands)
        return 'expert';
    const rep = propagate(cands, 5, constraint);
    return tierToDifficulty(rep.maxTier, rep.status === 'solved');
}
/**
 * The order in which cells fall to successively harder techniques — this is the
 * human-plausible solve path that puzzle bots follow (plan step 8).
 */
export function solvePath(grid, constraint) {
    const cands = candidatesFrom(grid);
    const order = [];
    if (!cands)
        return order;
    const known = new Set();
    for (let i = 0; i < CELLS; i++)
        if (grid[i] !== 0)
            known.add(i);
    for (;;) {
        const before = new Set(known);
        const rep = propagate(cands, 5, constraint);
        for (let i = 0; i < CELLS; i++) {
            if (!known.has(i) && popcount(cands[i]) === 1) {
                known.add(i);
                order.push(i);
            }
        }
        if (rep.status !== 'stuck' || known.size === before.size)
            break;
    }
    // Anything the technique set could not reach is appended in index order, so
    // the path always covers every cell.
    for (let i = 0; i < CELLS; i++)
        if (!known.has(i))
            order.push(i);
    return order;
}
//# sourceMappingURL=solver.js.map